import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, X } from 'lucide-react';
import { Button } from '../new-components/ui/button';
import { useUser } from '../../contexts/UserContext';
import { apiRequest } from '../../services/api';
import { BRAND_ASSETS, TENANT } from '../../config';
import { safeReturnTarget } from '../../config/authRoutes';
import { SSO_REQUIRED_COPY } from '../../services/ssoApi';
import { defaultDestination } from './postSignIn';
import { NetWorthStep } from './NetWorthStep';
import { AccreditationStep } from './AccreditationStep';
import { CreateLoginStep } from './CreateAccountStep';
import { SignInStep as SignInStepComponent } from './SignInStep';
import { SecuritiesDisclaimer } from './SecuritiesDisclaimer';
import { InlineError } from './InlineError';

interface AuthFlowProps {
  defaultMode?: 'signin' | 'signup';
}

// Signup collects the investor profile only. Once accreditation is submitted the user is
// registered and authenticated, and the route guard sends them to email verification / the
// portal — KYC and 2FA now live in Settings (AccountSettings), reached via the onboarding
// banner, so they are not steps in this wizard.
type SignUpStep = 'createLogin' | 'netWorth' | 'accreditation';
type SignInStep = 'signIn' | '2fa' | 'complete';

// Each step maps to a URL slug, so the flow lives in the address bar — browser Back/Forward,
// refresh, and deep-linking all work. Empty slug = the base route (/register or /login).
const SIGNUP_SLUGS: Record<SignUpStep, string> = {
  createLogin: '',
  netWorth: 'net-worth',
  accreditation: 'accreditation',
};
const SIGNIN_SLUGS: Record<SignInStep, string> = {
  signIn: '',
  '2fa': '2fa',
  complete: 'done',
};
// Reverse maps (slug → step) derived from the above so there's one source of truth.
const SIGNUP_SLUG_TO_STEP: Record<string, SignUpStep> = Object.fromEntries(
  Object.entries(SIGNUP_SLUGS).map(([step, slug]) => [slug, step as SignUpStep]),
);
const SIGNIN_SLUG_TO_STEP: Record<string, SignInStep> = Object.fromEntries(
  Object.entries(SIGNIN_SLUGS).map(([step, slug]) => [slug, step as SignInStep]),
);

// All signup steps are live steps now — drives the progress bar.
const LIVE_SIGNUP_STEPS: SignUpStep[] = ['createLogin', 'netWorth', 'accreditation'];

export function AuthFlow({ defaultMode = 'signup' }: AuthFlowProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isAuthenticated, user } = useUser();

  // The URL is the single source of truth for the current step (see SIGNUP_SLUGS): the step is
  // derived directly from the slug every render — no mirrored state to keep in sync — so
  // Back/Forward, refresh, and deep-links can't drift from what's rendered.
  const { step: stepSlug } = useParams<{ step?: string }>();
  const mode = defaultMode;
  const signUpStep: SignUpStep =
    mode === 'signup' ? (SIGNUP_SLUG_TO_STEP[stepSlug ?? ''] ?? 'createLogin') : 'createLogin';
  const signInStep: SignInStep =
    mode === 'signin' ? (SIGNIN_SLUG_TO_STEP[stepSlug ?? ''] ?? 'signIn') : 'signIn';
  const [loading, setLoading] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  // Bumped when the server says "this account signs in with Microsoft".
  const [ssoNudge, setSsoNudge] = useState(0);

  // Form data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [netWorth, setNetWorth] = useState('');
  const [accreditationCriteria, setAccreditationCriteria] = useState<Set<string>>(new Set());
  const [redirectMessage, setRedirectMessage] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  // Lost phone: password + backup code opens "register a new phone" — never a
  // session (security owner's ruling, 2026-09-16).
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');

  const registeringRef = useRef(false);

  const liveStepIndex = LIVE_SIGNUP_STEPS.indexOf(signUpStep);

  // Direction for the step transition: compare the incoming step's ordinal against the previous
  // render's, so forward moves slide in from the right and Back mirrors it — covering changeStep,
  // the Back button, and browser Back/Forward alike. Computed during render (the ref still holds
  // the prior ordinal here); the ref is advanced in an effect afterwards.
  const STEP_ORDER: string[] =
    mode === 'signup' ? ['createLogin', 'netWorth', 'accreditation'] : ['signIn', '2fa', 'complete'];
  const stepOrdinal = STEP_ORDER.indexOf(mode === 'signup' ? signUpStep : signInStep);
  const stepKey = `${mode}:${stepSlug ?? ''}`;

  // The enter animation is decided ONCE per step — when the URL step/mode actually changes — and
  // frozen for that mounted step's lifetime. Recomputing it every render would let an unrelated
  // re-render (e.g. typing in a field) swap the class and replay the animation. Mode entry
  // (sign-in ↔ create-account) and the first mount rise in; step-to-step within a flow slides
  // directionally (forward from the right, Back from the left).
  const prevKeyRef = useRef('');
  const prevOrdinalRef = useRef(stepOrdinal);
  const prevModeRef = useRef(mode);
  const enterClassRef = useRef('rise-in');
  if (stepKey !== prevKeyRef.current) {
    const firstMount = prevKeyRef.current === '';
    const modeChanged = !firstMount && prevModeRef.current !== mode;
    enterClassRef.current =
      firstMount || modeChanged
        ? 'rise-in'
        : stepOrdinal < prevOrdinalRef.current
        ? 'step-enter-back'
        : 'step-enter-fwd';
    prevKeyRef.current = stepKey;
    prevOrdinalRef.current = stepOrdinal;
    prevModeRef.current = mode;
  }
  const enterClass = enterClassRef.current;

  // Create-account + sign-in use a dynamic, fit-to-viewport column on MOBILE: the page is a
  // fixed-height (100dvh) flex column with a scrollable middle and the CTA riding at the
  // bottom — no fixed positioning, no hardcoded reserve, adapts to size/orientation. Desktop
  // (md+) keeps the centered layout untouched.
  const mobileFit =
    (mode === 'signup' && signUpStep === 'createLogin') ||
    (mode === 'signin' && signInStep === 'signIn');

  const basePath = mode === 'signup' ? '/register' : '/login';
  const pathForStep = (s: SignUpStep | SignInStep) => {
    const slug = mode === 'signup' ? SIGNUP_SLUGS[s as SignUpStep] : SIGNIN_SLUGS[s as SignInStep];
    return slug ? `${basePath}/${slug}` : basePath;
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;

    // Where they were trying to go always wins. This is also what makes a
    // per-department entry point work: bookmark /portal/patient-advocate,
    // sign in, and come back to it — ProtectedRoute carries the intent here.
    //
    // Validated, never trusted. The check here was `startsWith('/')`, which
    // accepts `//evil.example` and `/\evil.example` — both begin with a slash
    // and both are resolved by browsers as protocol-relative URLs to another
    // origin. That turns the sign-in page into an open redirect for anyone who
    // can put state on the navigation. `safeReturnPath` is the same primitive
    // D-4 gave the verification path.
    // The query rides along (2026-10-19: an email deep link's ?view=day&date=... or ?view=work&case=<id> must
    // survive sign-in); safeReturnTarget validates the path as safeReturnPath does and keeps only a plain query.
    const referredFrom = safeReturnTarget(location.state?.from);
    if (referredFrom) {
      navigate(referredFrom, { replace: true });
      return;
    }

    /*
     * Otherwise ask the server where this person belongs.
     *
     * What used to stand here read `users.role`, a column with two values —
     * admin and employee — which is the investor platform's idea of a person
     * and survived the eMerge migration unexamined. Every workforce account
     * that was not an admin went to /portal/invest/properties, which on this
     * tenant fell through to the demo shell (retired 2026-11-05) and rendered the Executive
     * Command Center. A patient advocate signed in, was shown an executive
     * dashboard offering to let her view as Executive, Strategy, Growth,
     * Operations or Engineering, and asked which one she was supposed to be.
     *
     * The answer is decided from ratified capability, server-side. If the
     * lookup fails we fall back to exactly the old behaviour rather than
     * holding someone at the door over a navigation question.
     */
    defaultDestination(user.role).then((destination) => {
      if (cancelled) return;
      navigate(destination, { replace: true });
    });
    return () => { cancelled = true; };
    // Only reads location.state — depending on the whole location object would re-run this
    // on every in-flow navigation.
  }, [isAuthenticated, user, navigate, location.state]);

  // Show message from redirect if present
  useEffect(() => {
    if (location.state?.message) {
      setRedirectMessage(location.state.message);
      if (location.state?.email) {
        setEmail(location.state.email);
      }
    }
  }, [location.state]);

  // Net-worth auto-advance timer: deferred and can outlive a fast unmount, so it's ref-tracked
  // and cleared on unmount (no stray navigate on an unmounted component).
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
  }, []);

  const changeStep = (newStep: SignUpStep | SignInStep) => {
    // Navigate straight to the step's URL; the derived step re-renders and its keyed wrapper
    // plays the directional enter animation (step-enter-fwd / -back, driven by enterDir).
    //
    // Where the person was going rides along (2026-10-19). Every workforce
    // sign-in passes through the 2fa step, and a bare navigate dropped the
    // router state ProtectedRoute put there, so a link from an email to one
    // case landed on the home page instead of the case.
    const from = location.state?.from;
    navigate(pathForStep(newStep), from ? { state: { from } } : undefined);
  };

  // Net-worth: briefly show the selected state, then advance. Timer is ref-tracked (cleared on
  // unmount, and before re-scheduling) rather than fire-and-forget.
  const handleNetWorthSelect = (val: string) => {
    setNetWorth(val);
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = setTimeout(() => changeStep('accreditation'), 200);
  };

  const handleBack = () => {
    // Each step is a real history entry now, so mirror the browser Back button; fall back to
    // home when there's nothing to go back to (e.g. deep-linked straight to a step).
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const handleSignIn = async (code?: string) => {
    if (loading) return;
    setSignInError(null);
    setLoading(true);
    try {
      const result = await login(email, password, code);
      switch (result.kind) {
        case 'requires2FA':
          changeStep('2fa');
          break;
        case 'success':
          changeStep('complete');
          break;
        case 'rateLimited':
          // Say what actually happened. Reporting this as a wrong password sent
          // the first person who hit it off to change a credential that worked.
          setSignInError(
            'Too many sign-in attempts from this network. Wait 15 minutes and try again — '
            + 'your password has not changed.'
          );
          break;
        case 'invalidCredentials':
          setSignInError(
            code
              ? 'Invalid authentication code. Please try again.'
              : 'Incorrect email or password. Please try again.'
          );
          break;
        default:
          // This account signs in with Microsoft: say so, and point at the
          // Microsoft button (SignInStep moves focus to it when the nudge changes).
          if (result.kind === 'accountState' && result.code === 'SSO_REQUIRED') {
            setSignInError(SSO_REQUIRED_COPY);
            setSsoNudge((n) => n + 1);
            break;
          }
          // accountState, serverError, offline — the server said something
          // materially different from "wrong password", and it says it better
          // than a generic client string can. `accountState` may also carry a
          // `code`; this surface does not need it, because the sentence is
          // already the actionable part.
          setSignInError(result.message);
      }
    } catch (error) {
      console.error('Login error:', error);
      setSignInError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setSignInError(null);
    if (!email || !password) {
      // The 2FA step keeps the password only in memory; a reload loses it.
      setSignInError('Enter your email and password again, then choose "Use a backup code".');
      setUseBackupCode(false);
      changeStep('signIn');
      return;
    }
    setLoading(true);
    const r = await apiRequest<{ ticket: string; remaining: number }>(
      'POST', '/activation/recover', { email, password, backupCode: backupCode.trim() }
    );
    setLoading(false);
    if (r.success && r.data?.ticket) {
      setPassword('');
      setBackupCode('');
      // Router state only: the ticket never enters the URL or storage.
      navigate('/activate/replace', { state: { ticket: r.data.ticket, remaining: r.data.remaining } });
      return;
    }
    setSignInError(r.status === undefined
      ? 'Could not reach the server. Check your connection and try again.'
      : (r.message || 'That backup code was not accepted.'));
  };

  const handleContinue = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (loading) return;

    if (mode === 'signup') {
      const steps: SignUpStep[] = ['createLogin', 'netWorth', 'accreditation'];
      const currentIndex = steps.indexOf(signUpStep);

      // Register user before KYC step so they're authenticated
      if (signUpStep === 'accreditation') {
        if (registeringRef.current) return;
        registeringRef.current = true;
        setLoading(true);
        try {
          const registrationData = {
            email,
            password,
            confirmPassword: password,
            firstName,
            lastName,
            accreditationStatus: accreditationCriteria.has('non-accredited')
              ? 'self_declared_non_accredited'
              : accreditationCriteria.size > 0
              ? 'self_declared_accredited'
              : 'pending',
            accreditationCriteria: Array.from(accreditationCriteria),
          };

          setRegistrationError(null);
          // register() throws with a human-readable message on failure; the catch below
          // shows it in the accreditation step's red inline message (no toast).
          // On success the user is authenticated — App's route guard redirects to email
          // verification / the portal, so there's no next step to advance to here.
          await register(registrationData);
        } catch (error: any) {
          console.error('Registration error:', error);
          setRegistrationError(error?.message || 'Something went wrong. Please try again.');
        } finally {
          setLoading(false);
          registeringRef.current = false;
        }
      } else if (currentIndex < steps.length - 1) {
        changeStep(steps[currentIndex + 1]);
      }
    } else {
      const steps: SignInStep[] = ['signIn', '2fa', 'complete'];
      const currentIndex = steps.indexOf(signInStep);

      if (currentIndex < steps.length - 1) {
        changeStep(steps[currentIndex + 1]);
      }
    }
  };

  const switchMode = (newMode: 'signin' | 'signup') => {
    // Navigating to the other base route mounts the flow fresh in the target mode.
    navigate(newMode === 'signup' ? '/register' : '/login');
  };

  return (
    <div
      className={`bg-background flex flex-col portal-typography ${
        mobileFit ? 'min-h-[100dvh] md:min-h-screen' : 'min-h-screen'
      }`}
    >
      {/* Header */}
      <div className="px-4 md:px-6 pt-4 md:pt-6 pb-3">
        {/* 3-column grid with equal 1fr sides keeps the logo dead-center regardless of
            whether the back button is shown, so it never twitches between steps. */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="justify-self-start">
            {((mode === 'signup' && signUpStep !== 'createLogin') ||
              (mode === 'signin' && signInStep === '2fa')) && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-foreground/70 hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
          </div>

          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img
              src={BRAND_ASSETS.logo.primary}
              alt={TENANT.branding.name}
              className="h-11 object-contain"
            />
          </Link>
        </div>

        {/* Compact progress stepper — lives in the header chrome so it doesn't eat content space */}
        {mode === 'signup' && liveStepIndex >= 0 && (
          <div className="max-w-md mx-auto mt-4 flex items-center gap-3">
            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
              Step {liveStepIndex + 1} of {LIVE_SIGNUP_STEPS.length}
            </span>
            <div className="flex-1 flex gap-1.5">
              {LIVE_SIGNUP_STEPS.map((step, i) => (
                <div
                  key={step}
                  className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                    i <= liveStepIndex ? 'bg-primary' : 'bg-secondary'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex justify-center px-4 md:px-6 md:items-center ${
          mobileFit
            ? 'items-stretch pb-0 md:pb-8'
            : 'items-start pt-2 md:pt-0 pb-8'
        }`}
      >
        <div
          key={stepKey}
          className={`w-full ${
            signUpStep === 'netWorth' || signUpStep === 'accreditation' ? 'max-w-xl self-stretch' : 'max-w-md'
          } ${mobileFit ? 'flex flex-col md:block' : ''} ${enterClass}`}
        >
          {/* Redirect message banner */}
          {redirectMessage && (
            <div className="mb-6 flex items-center justify-between gap-3 p-4 bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 rounded-xl text-[hsl(var(--success))]">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{redirectMessage}</span>
              </div>
              <button
                onClick={() => setRedirectMessage(null)}
                className="text-[hsl(var(--success))] hover:opacity-80 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {/* SIGN UP FLOW */}
          {mode === 'signup' && (
            <>
              {signUpStep === 'createLogin' && (
                <CreateLoginStep
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  password={password}
                  onFirstNameChange={setFirstName}
                  onLastNameChange={setLastName}
                  onEmailChange={setEmail}
                  onPasswordChange={setPassword}
                  onContinue={() => changeStep('netWorth')}
                  onSwitchToSignIn={() => switchMode('signin')}
                />
              )}

              {signUpStep === 'netWorth' && (
                <NetWorthStep
                  value={netWorth}
                  onSelect={handleNetWorthSelect}
                  onContinue={() => changeStep('accreditation')}
                />
              )}

              {signUpStep === 'accreditation' && (
                <AccreditationStep
                  selected={accreditationCriteria}
                  loading={loading}
                  error={registrationError}
                  onToggle={(id) => {
                    setRegistrationError(null);
                    // Functional update so AccreditationStep's rapid successive toggles
                    // (clear others → set this one) compose instead of clobbering each other.
                    setAccreditationCriteria((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id); else next.add(id);
                      return next;
                    });
                  }}
                  onSubmit={handleContinue}
                />
              )}

            </>
          )}

          {/* SIGN IN FLOW */}
          {mode === 'signin' && (
            <>
              {signInStep === 'signIn' && (
                <SignInStepComponent
                  email={email}
                  password={password}
                  loading={loading}
                  error={signInError}
                  onEmailChange={(v) => { setEmail(v); setSignInError(null); }}
                  onPasswordChange={(v) => { setPassword(v); setSignInError(null); }}
                  onSubmit={() => handleSignIn()}
                  onSwitchToSignUp={() => switchMode('signup')}
                  // The same carried destination the post-sign-in redirect uses (path + plain query, or null).
                  ssoReturnTo={safeReturnTarget(location.state?.from)}
                  ssoNudge={ssoNudge}
                />
              )}

              {signInStep === '2fa' && (
                <div className="space-y-8 max-w-md mx-auto">
                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                      <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h1 className="text-3xl font-medium">Two-Factor Authentication</h1>
                    <p className="text-muted-foreground">
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>
                  {!useBackupCode ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleSignIn(totpCode); }} className="space-y-6">
                    <div>
                      <label htmlFor="signin-totp" className="sr-only">Authenticator code</label>
                      <input
                        id="signin-totp"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        autoFocus
                        className="w-full px-6 py-4 text-2xl text-center tracking-[0.5em] rounded-xl border-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors border-border"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || totpCode.length !== 6}
                      className="w-full py-4 text-lg"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Verifying...
                        </span>
                      ) : 'Verify & Sign In'}
                    </Button>
                    <InlineError className="text-center text-sm">{signInError}</InlineError>
                    <p className="text-center text-sm text-muted-foreground">
                      Lost or replaced your phone?{' '}
                      <button
                        type="button"
                        className="underline"
                        onClick={() => { setUseBackupCode(true); setSignInError(null); }}
                      >
                        Use a backup code
                      </button>{' '}
                      to register a new one.
                    </p>
                  </form>
                  ) : (
                  <form onSubmit={handleBackupCode} className="space-y-5">
                    <p className="text-sm text-muted-foreground">
                      Enter one of the backup codes you saved when you set up your account.
                      It will not sign you in: it lets you register a new phone, and then you sign in with that.
                    </p>
                    <div>
                      <label htmlFor="signin-backup" className="block text-sm font-medium">Backup code</label>
                      <input
                        id="signin-backup"
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        value={backupCode}
                        onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                        placeholder="XXXXX-XXXXX"
                        autoFocus
                        className="mt-1 w-full px-4 py-3 text-lg text-center font-mono tracking-widest rounded-xl border-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors border-border"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || backupCode.replace(/[^A-Za-z0-9]/g, '').length < 8}
                      className="w-full py-4 text-lg"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Checking...
                        </span>
                      ) : 'Continue to register a new phone'}
                    </Button>
                    <InlineError className="text-center text-sm">{signInError}</InlineError>
                    <p className="text-center text-sm text-muted-foreground">
                      No codes left?{' '}
                      {TENANT.branding.supportEmail ? (
                        <>
                          Ask your administrator to reset your authenticator (
                          <a className="underline" href={`mailto:${TENANT.branding.supportEmail}`}>
                            {TENANT.branding.supportEmail}
                          </a>
                          ).
                        </>
                      ) : 'Ask your administrator to reset your authenticator.'}{' '}
                      <button type="button" className="underline" onClick={() => { setUseBackupCode(false); setSignInError(null); }}>
                        Use my authenticator instead
                      </button>
                    </p>
                  </form>
                  )}
                </div>
              )}

              {signInStep === 'complete' && (
                <div className="text-center space-y-8">
                  <div className="w-20 h-20 rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-10 h-10 text-[hsl(var(--success))]" />
                  </div>

                  <div className="space-y-4">
                    <h1 className="text-4xl md:text-5xl font-medium">
                      You're all set
                    </h1>
                    <p className="text-xl text-muted-foreground">
                      Redirecting to your portfolio...
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer — on mobile-fit steps the disclaimer is rendered inside the form instead,
          so hide this page-level copy there to avoid duplication. */}
      <SecuritiesDisclaimer className={`pb-6 px-4 ${mobileFit ? 'hidden md:block' : ''}`} />
    </div>
  );
}

export default AuthFlow;
